import React, { useRef, useMemo, useCallback, useState, useEffect, Fragment } from 'react';
import { useSelect } from 'downshift';
import '../FormControl/FormControl.es.js';
import { useFormControl } from '../FormControl/FormControlContext.es.js';
import { List } from '../List/List.es.js';
import { Scrollable } from '../Scrollable/Scrollable.es.js';
import { Input } from '../Input/Input.es.js';
import { PopoverBase } from '../PopoverBase/PopoverBase.es.js';
import '../../utils/assertions.es.js';
import '../../utils/responsive.es.js';
import '../../utils/scrollIntoView.es.js';
import 'polished';
import '@styled-system/css';
import 'styled-components';
import '../../utils/useEnhancedEffect.es.js';
import 'react-is';
import 'body-scroll-lock';
import '../../hooks/useNotification/useNotification.es.js';
import '../../hooks/useToast/useToast.es.js';
import '@popperjs/core';
import 'react-beautiful-dnd';
import 'nanoid/non-secure';
import { forwardRef } from '../../system/forwardRef.es.js';
import { omitSystemProps, getSystemProps } from '../../system/styled-system.es.js';
import { getGroups, getOptionsByGroup } from './utils.es.js';
import { SelectInputComponent } from './SelectInputComponent.es.js';
import { DefaultOptionComponent } from './DefaultOptionComponent.es.js';
import { DefaultValueComponent } from './DefaultValueComponent.es.js';

var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
function isValueDefined(value) {
  return typeof value !== "undefined";
}
const Select = forwardRef((props, ref) => {
  const _a = omitSystemProps(props), {
    options,
    shape = "default",
    size = "md",
    variant = "outlined",
    isInvalid,
    isValid,
    isRequired,
    isDisabled,
    isReadOnly,
    defaultValue,
    placeholder,
    groupBy,
    onChange,
    valueComponent: ValueComponent = DefaultValueComponent,
    optionComponent: OptionComponent = DefaultOptionComponent,
    popoverProps,
    disablePortal,
    usePortal = false,
    value
  } = _a, rest = __objRest(_a, [
    "options",
    "shape",
    "size",
    "variant",
    "isInvalid",
    "isValid",
    "isRequired",
    "isDisabled",
    "isReadOnly",
    "defaultValue",
    "placeholder",
    "groupBy",
    "onChange",
    "valueComponent",
    "optionComponent",
    "popoverProps",
    "disablePortal",
    "usePortal",
    "value"
  ]);
  const _b = getSystemProps(props), { color, width = "100%" } = _b, systemProps = __objRest(_b, ["color", "width"]);
  const anchorRef = useRef(null);
  const menuRef = useRef(null);
  const formControl = useFormControl({
    isInvalid,
    isValid,
    isRequired,
    isDisabled,
    isReadOnly
  });
  const groups = useMemo(() => getGroups(groupBy, options), [options, groupBy]);
  const optionsWithIndexes = useMemo(() => options.map((item, index) => __spreadProps(__spreadValues({}, item), {
    index
  })), [options]);
  const groupedOptions = useMemo(() => getOptionsByGroup(groupBy, groups, optionsWithIndexes), [groupBy, groups, optionsWithIndexes]);
  const getSelectedItemFromValue = useCallback((value2) => {
    return optionsWithIndexes.find((option) => option.value === value2) || null;
  }, [optionsWithIndexes]);
  const updateToDefaultItem = useCallback(() => {
    const defaultSelectedItem = getSelectedItemFromValue(defaultValue);
    setSelectedItem(defaultSelectedItem);
    if (onChange && defaultSelectedItem) {
      onChange(defaultSelectedItem);
    }
  }, [defaultValue, getSelectedItemFromValue, onChange]);
  const [selectedItem, setSelectedItem] = useState(() => {
    if (isValueDefined(value)) {
      return null;
    }
    return getSelectedItemFromValue(defaultValue);
  });
  useEffect(() => {
    if (isValueDefined(value)) {
      const newSelectedItem = getSelectedItemFromValue(value);
      if (!newSelectedItem || !newSelectedItem.disabled) {
        setSelectedItem(newSelectedItem);
      } else {
        updateToDefaultItem();
      }
    }
  }, [value, getSelectedItemFromValue, updateToDefaultItem]);
  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps, highlightedIndex } = useSelect({
    items: optionsWithIndexes,
    selectedItem,
    onSelectedItemChange: ({ selectedItem: selectedItem2 }) => {
      if (!selectedItem2) {
        return;
      }
      setSelectedItem(selectedItem2);
      if (onChange) {
        onChange(selectedItem2);
      }
    }
  });
  const [anchorWidth, setAnchorWidth] = useState();
  useEffect(() => {
    if (!selectedItem) {
      return;
    }
    const newSelectedItemByIndex = optionsWithIndexes[selectedItem.index];
    const newSelectedItemByValue = getSelectedItemFromValue(value);
    if (isValueDefined(value) && newSelectedItemByIndex !== newSelectedItemByValue) {
      return;
    }
    if (newSelectedItemByIndex !== selectedItem) {
      if (!newSelectedItemByIndex.disabled) {
        setSelectedItem(newSelectedItemByIndex);
      } else {
        updateToDefaultItem();
      }
    }
  }, [optionsWithIndexes, selectedItem, value, updateToDefaultItem, getSelectedItemFromValue]);
  useEffect(() => {
    if (!anchorRef.current) {
      return;
    }
    const currentSelectWidth = anchorRef.current.clientWidth;
    setAnchorWidth(currentSelectWidth);
  }, [isOpen]);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Input, __spreadProps(__spreadValues(__spreadProps(__spreadValues(__spreadValues({
    ref,
    wrapperRef: anchorRef,
    shape,
    size,
    variant,
    isInvalid: formControl.isInvalid,
    isDisabled: formControl.isDisabled,
    isReadOnly: formControl.isReadOnly,
    isRequired: formControl.isRequired
  }, getToggleButtonProps()), rest), {
    width
  }), systemProps), {
    as: SelectInputComponent
  }), selectedItem ? /* @__PURE__ */ React.createElement(ValueComponent, {
    selectedItem
  }) : placeholder), /* @__PURE__ */ React.createElement(PopoverBase, __spreadProps(__spreadValues({
    minWidth: anchorWidth,
    anchorRef,
    isOpen: !isDisabled && isOpen
  }, popoverProps), {
    offset: [0, 4],
    placement: "bottom",
    px: 0,
    py: 0,
    disablePortal,
    usePortal
  }), /* @__PURE__ */ React.createElement(Scrollable, null, /* @__PURE__ */ React.createElement(List, __spreadValues({}, getMenuProps({ ref: menuRef }, { suppressRefError: true })), groupedOptions.map((groupData, index) => /* @__PURE__ */ React.createElement(Fragment, {
    key: index
  }, groupData.group && /* @__PURE__ */ React.createElement(List.Subheader, null, groupData.group), groupData.options.map((option) => /* @__PURE__ */ React.createElement(List.Item, __spreadValues({
    key: option.index,
    isSelected: option.index === highlightedIndex,
    disabled: option.disabled
  }, getItemProps({
    item: option,
    index: option.index
  })), /* @__PURE__ */ React.createElement(OptionComponent, {
    option
  })))))))));
});
Select.displayName = "Select";
var Select$1 = Object.assign(Select, {});

export { Select$1 as default };
//# sourceMappingURL=Select.es.js.map
